import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { timeout } from "rxjs/operators";
import { environment } from "@env/environment";
import type { PhotoSection } from "@fsm/shared";
import { OfflineStorageService } from "./offline-storage.service";
import type { LocalExecutionTemplate } from "./offline-storage.types";

interface ExecutionTemplateApiItem {
  templateId: string;
  tenantId: string;
  name: string;
  checklistTemplates: { name: string; items: { name: string }[] }[];
  fieldDataSections: unknown[];
  /** Secciones de fotos configurables (spec configurable-photo-sections). Ausente = legacy. */
  photoSections?: PhotoSection[];
  isDeleted: boolean;
  updatedAt: string;
}

interface SyncResponse {
  data: {
    templates: ExecutionTemplateApiItem[];
    deletedIds: string[];
    /**
     * Continuation token when the backend paginates the full sync. The backend
     * now drains all pages server-side and omits this, but we still follow it
     * defensively so an older/paginated backend cannot silently truncate the
     * template set on the device (a missing template would drop the technician
     * to the wrong sections — see field-data resolution).
     */
    nextPageToken?: string;
  };
  requestId: string;
}

/** Safety cap on pages to follow, preventing an unbounded loop on a bad token. */
const MAX_SYNC_PAGES = 20;

const LAST_SYNC_KEY = "fsm_execution_templates_last_sync";

/**
 * ExecutionTemplateSyncService — Handles initial and delta sync of execution templates.
 *
 * On initial sync (no lastSyncedAt): GET /v1/execution-templates → store all.
 * On delta sync (has lastSyncedAt): GET /v1/execution-templates?updatedSince=<ts> → upsert + delete.
 * Integrated into the data sync flow after tenant config sync.
 */
@Injectable({ providedIn: "root" })
export class ExecutionTemplateSyncService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(OfflineStorageService);

  private readonly apiUrl = environment.apiUrl;

  /** Perform execution template sync (always full sync for fresh data) */
  async sync(): Promise<boolean> {
    try {
      // Always do a full sync to ensure fresh data from admin edits.
      // Templates payload is small (< 30KB typically) so bandwidth is not a concern.
      // Follow nextPageToken until exhausted so a paginated backend can never
      // truncate the template set on the device (a missing template would leave
      // an assigned OT unable to resolve its sections). The current backend
      // drains all pages server-side, so this loop normally runs exactly once.
      const templates: ExecutionTemplateApiItem[] = [];
      const deletedIds: string[] = [];
      let pageToken: string | undefined;
      let pages = 0;
      do {
        const url = pageToken
          ? `${this.apiUrl}/v1/execution-templates?nextPageToken=${encodeURIComponent(pageToken)}`
          : `${this.apiUrl}/v1/execution-templates`;
        const res: SyncResponse = await firstValueFrom(
          this.http.get<SyncResponse>(url).pipe(timeout(30_000)),
        );
        templates.push(...(res.data.templates ?? []));
        deletedIds.push(...(res.data.deletedIds ?? []));
        pageToken = res.data.nextPageToken;
        pages += 1;
      } while (pageToken && pages < MAX_SYNC_PAGES);

      // Clear existing and replace with fresh data from API
      if (templates.length > 0) {
        await this.storage.clearExecutionTemplates();
      }

      // Save all active templates and delete marked ones in parallel batches
      const savePromises: Promise<void>[] = [];
      const deletePromises: Promise<void>[] = [];

      for (const tpl of templates) {
        if (tpl.isDeleted) {
          deletePromises.push(
            this.storage.deleteExecutionTemplate(tpl.templateId),
          );
        } else {
          const local: LocalExecutionTemplate = {
            templateId: tpl.templateId,
            tenantId: tpl.tenantId,
            name: tpl.name,
            checklistTemplatesJson: JSON.stringify(tpl.checklistTemplates),
            fieldDataSectionsJson: JSON.stringify(tpl.fieldDataSections),
            photoSectionsJson: tpl.photoSections
              ? JSON.stringify(tpl.photoSections)
              : undefined,
            isDeleted: 0,
            updatedAt: tpl.updatedAt,
          };
          savePromises.push(this.storage.saveExecutionTemplate(local));
        }
      }

      // Remove explicitly deleted IDs
      for (const id of deletedIds ?? []) {
        deletePromises.push(this.storage.deleteExecutionTemplate(id));
      }

      // Execute all writes concurrently (SQLite handles serialization internally)
      await Promise.allSettled([...savePromises, ...deletePromises]);

      // Update last sync timestamp
      this.setLastSyncTimestamp(new Date().toISOString());
      return true;
    } catch {
      return false;
    }
  }

  /** Get the last sync timestamp from localStorage */
  private getLastSyncTimestamp(): string | null {
    try {
      return localStorage.getItem(LAST_SYNC_KEY);
    } catch {
      return null;
    }
  }

  /** Store the last sync timestamp in localStorage */
  private setLastSyncTimestamp(ts: string): void {
    try {
      localStorage.setItem(LAST_SYNC_KEY, ts);
    } catch {
      // localStorage unavailable
    }
  }
}
